import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import { SectionHeader } from '../components/SectionHeader';
import { SectionInfoBanner } from '../components/SectionInfoBanner';
import { FieldRow, ToggleCard } from '../components/ToggleCard';
import { StoreTabFooter } from '../components/StoreTabFooter';
import {
  MAX_PICKUP_FREE_ITEMS,
  PICKUP_FREE_RESERVED_ROW,
  SS_OUTLINED_INPUT,
  ssFieldSx,
  ssAddRowButtonSx,
} from '../storeSettingsConstants';

const sizeFieldSx = { width: '110px', '& .MuiInputBase-input': { fontSize: '14px' } };

/** Compact currency input on the right side of a FieldRow (2–4 digit amounts). */
const minSpendFieldSx = {
  width: 132,
  flexShrink: 0,
  '& .MuiInputBase-input': { fontSize: '14px', py: '8.5px', textAlign: 'right' },
};

export function PickupPromotionSection({
  meta,
  form,
  updatePickupPromoField,
  updateFreeItem,
  addFreeItem,
  removeFreeItem,
  tabFooterProps,
}) {
  const promo = form.pickup_order_promotion;
  const isNoneItem = (row) =>
    String(row?.name ?? '')
      .trim()
      .toLowerCase() === 'none' &&
    String(row?.size ?? '')
      .trim()
      .toLowerCase() === 'none';
  const visiblePickupItems = promo.pickup_free_items
    .map((item, idx) => ({ item, idx }))
    .filter(({ item, idx }) => idx === 0 || !isNoneItem(item));

  return (
    <>
      <SectionHeader Icon={meta.Icon} title={meta.title} subtitle={meta.subtitle} />

      <SectionInfoBanner title=''>
        <div>
          Enable <strong>‘Promotion Available’</strong> to offer this on pickup orders. If the order
          meets the minimum spend, customers can choose one free item from the list below.
        </div>
      </SectionInfoBanner>
      <ToggleCard
        label='Promotion available'
        description={
          <span>
            Enable to show a free item option to customers.
            <span className='store-settings-example-preview'>
              {' '}
              <span className='store-settings-example-preview__label'>Example</span>
              <img
                src='/images/promotion.png'
                alt='Pickup promotion example'
                className='store-settings-example-preview__image'
              />
            </span>
          </span>
        }
        value={promo.pickup_promo_available}
        onChange={(v) => updatePickupPromoField('pickup_promo_available', v)}
      />
      <FieldRow
        label='Minimum spend required for promotion'
        description='Customers must spend at least this amount to qualify for the promotion.'
      >
        <TextField
          id='pickup_min_spend'
          className={SS_OUTLINED_INPUT}
          size='small'
          hiddenLabel
          placeholder='0'
          value={promo.minimumAmountSpend}
          onChange={(e) => updatePickupPromoField('minimumAmountSpend', e.target.value)}
          inputProps={{
            inputMode: 'decimal',
            min: 0,
            max: 999,
            maxLength: 8,
            'aria-label': 'Minimum spend for promo in dollars',
          }}
          InputProps={{
            startAdornment: <InputAdornment position='start'>$</InputAdornment>,
          }}
          variant='outlined'
          sx={minSpendFieldSx}
        />
      </FieldRow>

      <div className='store-settings-list-block'>
        <div className='store-settings-list-header'>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#181436', marginBottom: 2 }}>
              Add or edit free items for pickup
            </div>
            <div
              style={{ fontSize: 12, color: '#6b7280', marginTop: '10px', marginBottom: '10px' }}
            >
              The first row is fixed ({PICKUP_FREE_RESERVED_ROW.name} /{' '}
              {PICKUP_FREE_RESERVED_ROW.size}
              ). Use ‘Add’ to include up to three additional items. You can edit or remove them.
            </div>
          </div>
          <span className='store-settings-count-badge'>
            {promo.pickup_free_items.length} / {MAX_PICKUP_FREE_ITEMS}
          </span>
        </div>

        {promo.pickup_free_items.length <= 1 && (
          <p className='store-settings-helper-text store-settings-empty-hint'>
            Optional: use &quot;Add free item&quot; to add up to three rows (beyond the fixed{' '}
            {PICKUP_FREE_RESERVED_ROW.name} / {PICKUP_FREE_RESERVED_ROW.size} row).
          </p>
        )}

        {visiblePickupItems.map(({ item, idx }) => {
          const isReserved = idx === 0 || isNoneItem(item);
          const displayName =
            idx === 0 && !isNoneItem(item)
              ? PICKUP_FREE_RESERVED_ROW.name
              : String(item?.name ?? '');
          const displaySize =
            idx === 0 && !isNoneItem(item)
              ? PICKUP_FREE_RESERVED_ROW.size
              : String(item?.size ?? '');

          return (
            <Box
              key={`free-${idx}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                gap: '0 12px',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <TextField
                id={`free_name_${idx}`}
                className={SS_OUTLINED_INPUT}
                size='small'
                label='Item name'
                value={displayName}
                onChange={(e) => updateFreeItem(idx, 'name', e.target.value)}
                placeholder={isReserved ? undefined : 'e.g. Orange Juice'}
                variant='outlined'
                sx={ssFieldSx}
                InputProps={isReserved ? { readOnly: true } : undefined}
              />
              <TextField
                id={`free_size_${idx}`}
                className={SS_OUTLINED_INPUT}
                size='small'
                label='Size'
                value={displaySize}
                onChange={(e) => updateFreeItem(idx, 'size', e.target.value)}
                placeholder={isReserved ? undefined : 'e.g. 200ML'}
                variant='outlined'
                sx={sizeFieldSx}
                InputProps={isReserved ? { readOnly: true } : undefined}
              />
              {isReserved ? (
                <Box sx={{ width: 34 }} aria-hidden />
              ) : (
                <IconButton
                  onClick={() => removeFreeItem(idx)}
                  color='error'
                  size='small'
                  aria-label={`Remove free item row ${idx + 1}`}
                >
                  <Delete fontSize='small' />
                </IconButton>
              )}
            </Box>
          );
        })}

        <Button
          variant='contained'
          startIcon={<Add />}
          onClick={addFreeItem}
          disabled={promo.pickup_free_items.length >= MAX_PICKUP_FREE_ITEMS}
          sx={ssAddRowButtonSx}
        >
          Add free item
        </Button>
      </div>

      <StoreTabFooter tabId='5' {...tabFooterProps} />
    </>
  );
}
